import React, { useState } from 'react'

import CLOSE_WHITE_ICON from '@public/assets/common/ai_close_white.svg'
import { useDeviceStore } from '@/stores/useDeviceStore'
import { useUserStore } from '@/stores/useUserStore'
import { cn } from '@/utils/mergeClassNames'

const PROGRESS_STEP = 20
const MAX_IN_PROGRESS_PERCENTAGE = 80

function MiralinkAutoDeployBar() {
  const [isDismissed, setIsDismissed] = useState(false)
  const isMobile = useDeviceStore(state => state.isMobile)

  const selfUser = useUserStore(state => state.selfUser)
  const miralink = selfUser?.account?.metadata?.miralink
  const syncStatuses = miralink?.sync_status
    ? [
        miralink.sync_status.users,
        miralink.sync_status.clients,
        miralink.sync_status.matters,
        miralink.sync_status.contacts,
        miralink.sync_status.rates,
        miralink.sync_status.codes,
      ]
    : []
  const completedSyncCount = syncStatuses.filter(
    status => status === 'done'
  ).length
  const progressPercentage = Math.min(
    MAX_IN_PROGRESS_PERCENTAGE,
    Math.max(
      PROGRESS_STEP,
      Math.ceil(
        ((completedSyncCount / Math.max(syncStatuses.length, 1)) *
          MAX_IN_PROGRESS_PERCENTAGE) /
          PROGRESS_STEP
      ) * PROGRESS_STEP
    )
  )
  const areAllSyncsDone =
    syncStatuses.length > 0 && completedSyncCount === syncStatuses.length
  const isDeploymentDone = miralink?.deployment_status === 'done'
  const isAutoDeployComplete = areAllSyncsDone && isDeploymentDone

  if (!miralink || isDismissed || isAutoDeployComplete) return null

  return (
    <div
      role="alert"
      className={cn(
        'relative flex w-full justify-between gap-1 bg-[#FFA007] px-5 py-[15px] text-[16px] font-normal leading-5 text-white shadow-sm',
        isMobile ? 'items-stretch' : 'items-center'
      )}
    >
      <span className={cn('min-w-0 flex-1 break-words', isMobile && 'pr-2')}>
        {`Matter and Client data are currently being synchronized from your billing system. Search results may be incomplete until the process is finished.`}
      </span>

      <div
        className={cn(
          'relative ml-2 shrink-0',
          isMobile ? 'w-[41px]' : 'flex items-center gap-[15px]'
        )}
      >
        <span
          className={cn(
            'font-bold text-[17px]',
            isMobile
              ? 'absolute bottom-0 right-0 leading-none'
              : 'w-[41px] text-center'
          )}
        >
          {progressPercentage}%
        </span>

        <button
          type="button"
          className={cn(
            'inline-flex h-8 shrink-0 cursor-pointer rounded-full border-0 bg-transparent p-0',
            isMobile
              ? 'absolute right-0 top-0 w-8 items-start justify-end'
              : 'items-center justify-center'
          )}
          aria-label="Close MIRA notification"
          onClick={() => setIsDismissed(true)}
        >
          <img className="h-4 w-4" src={CLOSE_WHITE_ICON} alt="" />
        </button>
      </div>

      <div
        role="progressbar"
        aria-label="MIRA data synchronization progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progressPercentage}
        className="absolute inset-x-0 bottom-0 h-1.5 overflow-hidden bg-white/70"
      >
        <div
          className="h-full bg-[#D17800] transition-[width] duration-300"
          style={{ width: `${progressPercentage}%` }}
        />
      </div>
    </div>
  )
}

export default MiralinkAutoDeployBar
